import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaCheckCircle, FaHome, FaFilePdf, FaEnvelope } from 'react-icons/fa';
import { PDFDownloadLink } from '@react-pdf/renderer';
import ReceiptPDF from '../ui/ReceiptPDF';
import confetti from 'canvas-confetti';

const PRIMARY_COLOR = '#182848';

// --- Styled Components (unchanged) ---
const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f8ff, #e6f0ff);
  position: relative;
  padding: 1rem;
  box-sizing: border-box;
`;

const BlobBackground = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 70%;
  height: 100%;
  background: linear-gradient(135deg, #e6f7ff, #d4edff);
  clip-path: polygon(50% 0%, 100% 0, 100% 100%, 0% 100%);
  z-index: -1;
  opacity: 0.7;

  @media (max-width: 768px) {
    width: 100%;
    opacity: 0.5;
  }
`;

const ThankYouCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.14);
  padding: 2.5rem;
  max-width: 600px;
  width: 100%;
  text-align: center;
  position: relative;
  z-index: 1;
  border: 1px solid #e0e4ea;

  animation: fadeIn 0.6s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 576px) {
    padding: 2rem 1.5rem;
    border-radius: 14px;
  }
`;

const SuccessIcon = styled(FaCheckCircle)`
  font-size: 4rem;
  color: #28a745;
  margin-bottom: 1rem;
  animation: float 3s ease-in-out infinite;

  @keyframes float {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-10px);
    }
  }

  @media (max-width: 576px) {
    font-size: 3.5rem;
  }
`;

const Title = styled.h1`
  font-size: 2.3rem;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
  font-weight: 700;

  @media (max-width: 576px) {
    font-size: 1.9rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #555;
  margin-bottom: 1.8rem;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 576px) {
    font-size: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const DetailList = styled.div`
  text-align: left;
  background: #f8f9fa;
  border-radius: 12px;
  padding: 1rem 1.25rem;
  margin: 1.5rem 0;
  font-size: 0.95rem;
  color: #444;
  border: 1px solid #e9ecef;

  p {
    margin: 0.5rem 0;
    word-break: break-word;
  }

  strong {
    color: #222;
    min-width: 120px;
    display: inline-block;
  }

  @media (max-width: 576px) {
    padding: 1rem;
    font-size: 0.9rem;
  }
`;

const ActionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: center;
    gap: 1.2rem;
  }
`;

const StyledButton = styled.button`
  background-color: ${PRIMARY_COLOR};
  color: white;
  border: none;
  border-radius: 10px;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #132038;
    transform: translateY(-2px);
    box-shadow: 0 6px 14px rgba(24, 40, 72, 0.2);
  }

  &:focus {
    outline: 2px solid #3a5080;
  }

  &.outline {
    background-color: transparent;
    color: ${PRIMARY_COLOR};
    border: 2px solid ${PRIMARY_COLOR};

    &:hover {
      background-color: rgba(24, 40, 72, 0.1);
      color: #132038;
    }
  }
`;

const ContactLink = styled.a`
  color: ${PRIMARY_COLOR};
  text-decoration: none;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  margin-top: 1.2rem;
  font-size: 0.95rem;

  &:hover {
    text-decoration: underline;
  }
`;

const FallbackCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 400px;
  margin: 2rem auto;
  text-align: center;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  color: #555;
`;
// --- End of Styles ---

const ThankYouPage = () => {
  const location = useLocation();
  const { application, orderId, amount, paidAt, email } = location.state || {};

  const isValid = Boolean(orderId && amount && application);

  React.useEffect(() => {
    if (isValid) {
      confetti({
        particleCount: 120,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#28a745', '#182848', '#007BFF', '#FFD700'],
        ticks: 200,
      });
    }
  }, [isValid]); 

  if (!isValid) {
    return (
      <PageWrapper>
        <BlobBackground />
        <FallbackCard>
          <FaCheckCircle size={48} color="#dc3545" style={{ marginBottom: '1rem' }} />
          <h4>Invalid Access</h4>
          <p className="text-muted">No payment details found.</p>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <StyledButton style={{ marginTop: '1rem' }}>
              <FaHome /> Go Home
            </StyledButton>
          </Link>
        </FallbackCard>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <BlobBackground />
      <ThankYouCard>
        <SuccessIcon aria-hidden="true" />
        <Title>Thank You!</Title>
        <Subtitle>We've received your payment and appreciate your trust in us.</Subtitle>

        <DetailList>
          <p><strong>Order ID:</strong> {orderId}</p>
          <p><strong>Amount Paid:</strong> ₹{amount.toLocaleString()}</p>
          <p><strong>Date & Time:</strong> {new Date(paidAt).toLocaleString('en-IN')}</p>
          <p><strong>Email:</strong> {email}</p>
        </DetailList>

        <ActionGroup>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <StyledButton type="button">
              <FaHome /> Go to Home
            </StyledButton>
          </Link>

          <PDFDownloadLink
            document={<ReceiptPDF application={application} />}
            fileName={`Receipt_${orderId}.pdf`}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            {({ loading }) => (
              <StyledButton type="button" className="outline">
                <FaFilePdf /> {loading ? 'Preparing...' : 'Download Receipt'}
              </StyledButton>
            )}
          </PDFDownloadLink>
        </ActionGroup>

        <ContactLink href={`mailto:info@q2d.in?subject=Payment Support - ${orderId}`}>
          <FaEnvelope /> Contact Support
        </ContactLink>
      </ThankYouCard>
    </PageWrapper>
  );
};

export default ThankYouPage;